import { useEffect } from 'react';
import { NewCompetency } from 'domain/people/career/qualification/newCompetency/newCompetencyService';
import { FormattedTextField, TextField } from 'framework/ui/field';
import { ControlledList, DataGridLayout } from 'framework/list';
import EmptyQualificationLevelMessage from 'domain/people/career/qualification/model/shared/EmptyQualificationLevelMessage';
import { Box, Typography } from '@mui/material';
import { Collapse, CollapseControls, useCollapse } from 'framework/ui/mui-builder';

const NewCompetencyQualificationModel = ({ data, title }: { data: NewCompetency[]; title: string }) => {
  const { expandAll, collapseAll, toggle, isExpanded, areAllItemsExpanded, areAllItemsCollapsed } = useCollapse();
  const competencyIds = data.map(({ id }) => String(id));

  useEffect(() => {
    collapseAll();
  }, [title, collapseAll]);

  const collapseControls = (
    <CollapseControls
      isExpandButtonShown={!areAllItemsExpanded(competencyIds)}
      isCollapseButtonShown={!areAllItemsCollapsed()}
      expandAll={() => expandAll(competencyIds)}
      collapseAll={collapseAll}
    />
  );

  return (
    <Box
      sx={theme => ({
        '& table.list-general': {
          [theme.breakpoints.down('xxl')]: {
            minWidth: '1080px',
          },
        },
        '& .formatted-text-list': {
          '& td span': { whiteSpace: 'pre-wrap' },
          '& .th-normal': { minWidth: '100px !important' },
        },
      })}
    >
      <Box
        mt={1.5}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}
      >
        <Typography variant="h4" mb={2}>
          {title}
        </Typography>
        <Box mb={2}>{collapseControls}</Box>
      </Box>
      <Box mb={1.5}>
        {data.map(({ id, name: levelTitle, levels }) => (
          <Collapse
            key={`qualification-${id}`}
            header={
              <Box className="collapse-title-wrapper" m={0}>
                <Typography variant="h4" m={0} className="collapse-title">
                  {levelTitle}
                </Typography>
              </Box>
            }
            isExpanded={isExpanded(String(id))}
            toggle={() => {
              toggle(String(id));
            }}
          >
            {levels.length ? (
              <ControlledList data={{ items: levels, totalElements: levels.length, totalPages: 1 }}>
                <DataGridLayout
                  showTotal={false}
                  tableWrapperClassName="formatted-text-list"
                  sxPaginationWrapper={{ display: 'none' }}
                  sxResponsiveWrapper={{
                    mb: 2,
                    '& tr:last-child td': { borderBottom: 0 },
                  }}
                >
                  <FormattedTextField source="levelNumber" label=" " width={30} format={(str: string) => `L${str}`} />
                  <TextField source="indicator" label="Indicator" width={300} />
                  <TextField source="assessmentGuidelines" label="Assessment Guidelines" width={350} />
                </DataGridLayout>
              </ControlledList>
            ) : (
              <EmptyQualificationLevelMessage sx={{ paddingTop: '15px', paddingBottom: '30px' }} />
            )}
          </Collapse>
        ))}
      </Box>
      <Box display="flex" mt={2} sx={{ justifyContent: { md: 'flex-end' } }}>
        {collapseControls}
      </Box>
    </Box>
  );
};

export default NewCompetencyQualificationModel;
